import React from 'react';
import {
  Box,
  Container,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  Link,
  Button,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';

// Assets
import logo from '../assets/bouldou white.png';

const primaryColor = '#B200BB';
const secondaryColor = '#FF3B30';

const styles = {
  root: {
    minHeight: '100vh',
    background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
    display: 'flex',
    alignItems: 'center',
    py: { xs: 4, sm: 6 },
    fontFamily: '"Poppins", Arial, sans-serif',
  },
  container: {
    background: '#fff',
    borderRadius: '24px',
    boxShadow: '0 25px 70px rgba(0,0,0,0.22)',
    p: { xs: 4, sm: 6 },
    maxWidth: '800px',
  },
  logo: {
    height: '50px',
    mb: 3,
  },
  title: {
    fontWeight: 800,
    fontSize: { xs: '2.2rem', sm: '2.8rem' },
    background: `linear-gradient(45deg, ${primaryColor}, ${secondaryColor})`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    textAlign: 'center',
    mb: 2,
  },
  effectiveDate: {
    textAlign: 'center',
    color: primaryColor,
    fontWeight: 600,
    fontSize: '1.1rem',
    mb: 4,
  },
  introText: {
    fontSize: '1.1rem',
    lineHeight: 1.8,
    color: '#333',
    textAlign: 'center',
    mb: 4,
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: '1.4rem',
    color: primaryColor,
    mt: 5,
    mb: 2,
  },
  text: {
    color: '#444',
    lineHeight: 1.8,
    fontSize: '1rem',
  },
  listItem: {
    alignItems: 'flex-start',
    py: 0.8,
  },
  companyInfo: {
    background: '#f9f0ff',
    p: 3,
    borderRadius: '16px',
    textAlign: 'center',
    my: 4,
    border: `2px solid ${primaryColor}20`,
  },
  backButton: {
    mt: 6,
    borderRadius: '16px',
    py: 1.8,
    fontWeight: 600,
    fontSize: '1.2rem',
    background: `linear-gradient(45deg, ${primaryColor}, ${secondaryColor})`,
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: '0 15px 35px rgba(178,0,187,0.5)',
    },
  },
};

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <Box sx={styles.root}>
      <Container maxWidth="md">
        <Box sx={styles.container}>
          {/* Logo */}
          <Box textAlign="center">
            <img src={logo} alt="Bouldou" style={styles.logo} />
          </Box>

          <Typography variant="h1" sx={styles.title}>
            Privacy Policy
          </Typography>

          <Typography sx={styles.effectiveDate}>
            Effective Date: August 30th, 2025
          </Typography>

          <Typography sx={styles.introText}>
            Welcome to Bouldou’s Privacy Policy. Thank you for taking the time to read it.<br />
            We appreciate that you trust us with your information and we intend to always keep that trust.
          </Typography>

          <Divider sx={{ my: 4 }} />

          {/* 1. Who We Are */}
          <Typography sx={styles.sectionTitle}>1. Who We Are</Typography>
          <Box sx={styles.companyInfo}>
            <Typography fontWeight={700} color={primaryColor}>
              BOULDOU.SC PTY LTD
            </Typography>
            <Typography>Anse Etoile, Mahé, Seychelles</Typography>
            <Typography>
              Email: <Link href="mailto:contactbouldou@gmail.com" color={primaryColor}>contactbouldou@gmail.com</Link>
            </Typography>
          </Box>

          {/* 2. Where This Policy Applies */}
          <Typography sx={styles.sectionTitle}>2. Where This Privacy Policy Applies</Typography>
          <Typography sx={styles.text} paragraph>
            This Privacy Policy applies to websites, apps, events, and other services operated by Bouldou. For simplicity, we refer to all of these as our “services”. We’ve added links to this Privacy Policy on all applicable services.
          </Typography>

          {/* 3. Information We Collect */}
          <Typography sx={styles.sectionTitle}>3. Information We Collect</Typography>
          <Typography sx={styles.text} paragraph>
            We collect information you give us directly, information from others, and information generated when you use our services.
          </Typography>

          {/* 4. Information You Give Us */}
          <Typography sx={styles.sectionTitle}>4. Information You Give Us</Typography>
          <Typography sx={styles.text} paragraph>
            When you use Bouldou, you provide:
          </Typography>
          <List>
            <ListItem sx={styles.listItem}><ListItemText primary="• Login credentials, gender, date of birth, and photo (verified by Administration to ensure authenticity and safety)" /></ListItem>
            <ListItem sx={styles.listItem}><ListItemText primary="• Profile details: personality, lifestyle, interests, photos, and videos" /></ListItem>
            <ListItem sx={styles.listItem}><ListItemText primary="• Payment information when you make purchases" /></ListItem>
            <ListItem sx={styles.listItem}><ListItemText primary="• Responses to surveys, promotions, or customer support interactions" /></ListItem>
            <ListItem sx={styles.listItem}><ListItemText primary="• Chats and content you publish on the platform" /></ListItem>
          </List>
          <Typography sx={styles.text} paragraph>
            <strong>Note:</strong> Photo verification is manually approved by Administration to maintain a safe, authentic, and Seychellois-only community.
          </Typography>

          {/* 5–6. Information from Others & Usage */}
          <Typography sx={styles.sectionTitle}>5–6. Information from Others & When You Use Our Services</Typography>
          <Typography sx={styles.text} paragraph>
            We also collect:
          </Typography>
          <List>
            <ListItem sx={styles.listItem}><ListItemText primary="• Reports or messages from other users" /></ListItem>
            <ListItem sx={styles.listItem}><ListItemText primary="• Usage data (logins, features used, interactions)" /></ListItem>
            <ListItem sx={styles.listItem}><ListItemText primary="• Device info (IP, browser, OS, advertising IDs)" /></ListItem>
            <ListItem sx={styles.listItem}><ListItemText primary="• Geolocation (only with your consent)" /></ListItem>
          </List>

          {/* 7. Cookies */}
          <Typography sx={styles.sectionTitle}>7. Cookies & Similar Technologies</Typography>
          <Typography sx={styles.text} paragraph>
            We use cookies to authenticate you, remember preferences, analyze traffic, and deliver personalized ads. You can control cookies via your browser settings.
          </Typography>

          {/* 8. How We Use Information */}
          <Typography sx={styles.sectionTitle}>8. How We Use Your Information</Typography>
          <Typography sx={styles.text} paragraph>
            We use your data to:
          </Typography>
          <List>
            <ListItem sx={styles.listItem}><ListItemText primary="• Create and manage your account" /></ListItem>
            <ListItem sx={styles.listItem}><ListItemText primary="• Help you connect with others" /></ListItem>
            <ListItem sx={styles.listItem}><ListItemText primary="• Improve safety and fight fraud" /></ListItem>
            <ListItem sx={styles.listItem}><ListItemText primary="• Show relevant ads and offers" /></ListItem>
            <ListItem sx={styles.listItem}><ListItemText primary="• Develop new features and services" /></ListItem>
          </List>

          {/* 9. How We Share Information */}
          <Typography sx={styles.sectionTitle}>9. How We Share Information</Typography>
          <Typography sx={styles.text} paragraph>
            We share your information with:
          </Typography>
          <List>
            <ListItem sx={styles.listItem}><ListItemText primary="• Other users (your public profile and content)" /></ListItem>
            <ListItem sx={styles.listItem}><ListItemText primary="• Trusted service providers (hosting, analytics, payments)" /></ListItem>
            <ListItem sx={styles.listItem}><ListItemText primary="• Law enforcement when required" /></ListItem>
            <ListItem sx={styles.listItem}><ListItemText primary="• In case of merger or acquisition" /></ListItem>
          </List>

          {/* 10. Your Rights */}
          <Typography sx={styles.sectionTitle}>10. Your Rights</Typography>
          <Typography sx={styles.text} paragraph>
            You can access, update, or delete your data directly in the app. You control device permissions and can delete your account anytime.
          </Typography>

          {/* 11–13. Security, Retention, Children */}
          <Typography sx={styles.sectionTitle}>11–13. Security, Data Retention & Children's Privacy</Typography>
          <Typography sx={styles.text} paragraph>
            We use strong security measures to protect your data. We keep your information only as long as needed (usually deleted after account deletion or 2 years of inactivity). Bouldou is strictly for users 18+ — we do not collect data from children.
          </Typography>

          {/* 14. Changes */}
          <Typography sx={styles.sectionTitle}>14. Privacy Policy Changes</Typography>
          <Typography sx={styles.text} paragraph>
            We may update this policy. Material changes will be notified in advance via email or in-app message.
          </Typography>

          {/* Back to Home Button */}
          <Box sx={{ mt: 6, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3, justifyContent: 'center' }}>
            <Button
              variant="outlined"
              size="large"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/')}
              fullWidth
              sx={{
                py: 1.8,
                borderRadius: '16px',
                fontWeight: 700,
                fontSize: '1.1rem',
                border: `2.5px solid ${primaryColor}`,
                color: primaryColor,
                '&:hover': {
                  borderColor: primaryColor,
                  bgcolor: `${primaryColor}10`,
                  transform: 'translateY(-2px)',
                },
              }}
            >
              Back to Home
            </Button>

            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/signup')}
              fullWidth
              sx={{
                py: 1.8,
                borderRadius: '16px',
                fontWeight: 700,
                fontSize: '1.1rem',
                background: `linear-gradient(45deg, ${primaryColor}, ${secondaryColor})`,
                boxShadow: '0 10px 30px rgba(178, 0, 187, 0.4)',
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: '0 15px 35px rgba(178, 0, 187, 0.5)',
                },
              }}
            >
              Create Account
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;